import { Button, ToggleGroup, ToggleGroupItem } from '@monksflow/monks-ui';
import { LayoutGridIcon, ListIcon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FilterChipPopover, SearchInput, type FilterChipOption } from '@/shared/ui';
import type { AudienceViewMode } from '../../model';
import type { AudienceCreationStatus } from '../AudienceStatusChip';
import type { AudiencesToolbarV2Props } from './AudiencesToolbarV2.types';

const STATUS_VALUES: AudienceCreationStatus[] = ['ready', 'processing', 'failed'];

export function AudiencesToolbarV2({
  search,
  onSearchChange,
  selectedStatuses,
  onSelectedStatusesChange,
  viewMode,
  onViewModeChange,
  onCreateClick,
}: AudiencesToolbarV2Props) {
  const { t } = useTranslation('audiences');

  const statusOptions: FilterChipOption<AudienceCreationStatus>[] = STATUS_VALUES.map((status) => ({
    value: status,
    label: t(`audiences.audiencesToolbarV2.status.${status}`),
  }));

  return (
    <div className="flex flex-wrap items-center gap-app-inset px-section py-section">
      <SearchInput
        className="w-80"
        value={search}
        onChange={onSearchChange}
        placeholder={t('audiences.audiencesToolbarV2.searchAudiences')}
        aria-label={t('audiences.audiencesToolbarV2.searchAudiencesLabel')}
      />

      <FilterChipPopover
        mode="multiple"
        label={t('audiences.audiencesToolbarV2.status.label')}
        options={statusOptions}
        value={selectedStatuses}
        onChange={(next) => onSelectedStatusesChange([...next] as AudienceCreationStatus[])}
        searchable={false}
      />

      <ToggleGroup
        type="single"
        variant="outline"
        value={viewMode}
        // Radix reports deselection of the active item as an empty string; the
        // list always needs a shape, so only a real choice is passed on.
        onValueChange={(next) => next && onViewModeChange(next as AudienceViewMode)}
        aria-label={t('audiences.audiencesToolbarV2.viewMode')}
        className="ml-auto"
      >
        {/* eslint-disable-next-line i18next/no-literal-string -- AudienceViewMode values, not copy */}
        <ToggleGroupItem value="grid" aria-label={t('audiences.audiencesToolbarV2.gridView')}>
          <LayoutGridIcon />
        </ToggleGroupItem>
        {/* eslint-disable-next-line i18next/no-literal-string -- AudienceViewMode values, not copy */}
        <ToggleGroupItem value="table" aria-label={t('audiences.audiencesToolbarV2.listView')}>
          <ListIcon />
        </ToggleGroupItem>
      </ToggleGroup>

      {onCreateClick && (
        <Button size="default" onClick={onCreateClick}>
          <PlusIcon className="size-4" />
          {t('audiences.audiencesToolbarV2.createAudience')}
        </Button>
      )}
    </div>
  );
}

AudiencesToolbarV2.displayName = 'AudiencesToolbarV2';
